import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { FounderConfirmPublish } from "../../../components/founder/FounderConfirmPublish";
import { FounderIconButton } from "../../../components/founder/FounderIconButton";
import {
  FounderOperationalShell,
  FounderSplitPanel,
} from "../../../components/founder/FounderOperationalShell";
import {
  FounderEmptyState,
  FounderErrorState,
  FounderLoadingState,
} from "../../../components/founder/FounderWorkspaceChrome";
import {
  fetchFounderPublisherDetail,
  fetchFounderPublishers,
  fetchFounderWorkspaceCounts,
  updateFounderPublisherStatus,
  type FounderPublisherDetail,
  type FounderPublisherRow,
} from "../../../founder/founderWorkspaceApi";

export function FounderPublishersWorkspace() {
  const [searchParams, setSearchParams] = useSearchParams();
  const filter = searchParams.get("filter") ?? "all";
  const selectedId = searchParams.get("publisher");
  const query = searchParams.get("q") ?? "";

  const [counts, setCounts] = useState<Awaited<ReturnType<typeof fetchFounderWorkspaceCounts>> | null>(null);
  const [publishers, setPublishers] = useState<FounderPublisherRow[]>([]);
  const [publisherDetail, setPublisherDetail] = useState<FounderPublisherDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [confirmAction, setConfirmAction] = useState<"active" | "suspended" | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState("");

  useEffect(() => {
    Promise.all([fetchFounderPublishers(), fetchFounderWorkspaceCounts()])
      .then(([rows, workspaceCounts]) => {
        setPublishers(rows);
        setCounts(workspaceCounts);
        setError("");
      })
      .catch(() => setError("Could not load publishers."))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!selectedId) {
      setPublisherDetail(null);
      return;
    }
    fetchFounderPublisherDetail(selectedId)
      .then(setPublisherDetail)
      .catch(() => setPublisherDetail(null));
  }, [selectedId]);

  const visible = useMemo(() => {
    let rows = publishers;
    if (filter === "active") {
      rows = rows.filter((row) => row.account_status === "active");
    } else if (filter === "suspended") {
      rows = rows.filter((row) => row.account_status === "suspended");
    }
    if (query.trim()) {
      const needle = query.trim().toLowerCase();
      rows = rows.filter(
        (row) =>
          row.email.toLowerCase().includes(needle) ||
          row.full_name.toLowerCase().includes(needle) ||
          row.display_name.toLowerCase().includes(needle),
      );
    }
    return rows;
  }, [filter, publishers, query]);

  const selected = publishers.find((row) => row.id === selectedId) ?? null;

  function setFilter(next: string) {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set("filter", next);
    setSearchParams(nextParams, { replace: true });
  }

  function openPublisher(id: string) {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set("publisher", id);
    setSearchParams(nextParams, { replace: true });
  }

  function closePublisher() {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete("publisher");
    setSearchParams(nextParams, { replace: true });
  }

  async function applyStatus(status: "active" | "suspended") {
    if (!selected) {
      return;
    }
    setSubmitting(true);
    try {
      await updateFounderPublisherStatus(selected.id, status);
      setPublishers((rows) =>
        rows.map((row) => (row.id === selected.id ? { ...row, account_status: status } : row)),
      );
      setResult(status === "active" ? "Publisher restored." : "Publisher suspended.");
      setConfirmAction(null);
    } catch {
      setResult("Status update failed.");
    } finally {
      setSubmitting(false);
    }
  }

  const metrics = counts
    ? [
        { id: "a", label: "Active", value: counts.activePublishers, tone: "mint" as const },
        { id: "s", label: "Suspended", value: counts.suspendedPublishers, tone: "coral" as const },
        { id: "t", label: "Total listed", value: publishers.length, tone: "steel" as const },
      ]
    : [];

  const toolbar = (
    <>
      <div className="oac-founder-ops-filter-row">
        {(["all", "active", "suspended"] as const).map((item) => (
          <button
            className={filter === item ? "oac-founder-ops__tab oac-founder-ops__tab--active" : "oac-founder-ops__tab"}
            key={item}
            onClick={() => setFilter(item)}
            type="button"
          >
            {item === "all" ? "All" : item === "active" ? "Active" : "Suspended"}
          </button>
        ))}
      </div>
      <input
        aria-label="Search publishers"
        className="oac-founder-ops-search"
        onChange={(e) => {
          const nextParams = new URLSearchParams(searchParams);
          if (e.target.value) {
            nextParams.set("q", e.target.value);
          } else {
            nextParams.delete("q");
          }
          setSearchParams(nextParams, { replace: true });
        }}
        placeholder="Search publishers"
        type="search"
        value={query}
      />
    </>
  );

  return (
    <FounderOperationalShell metrics={metrics} title="Publishers" toolbar={toolbar}>
      <FounderSplitPanel
        onCloseSecondary={closePublisher}
        primary={
          <>
            {loading ? <FounderLoadingState /> : null}
            {!loading && error ? <FounderErrorState message={error} /> : null}
            {!loading && !error && visible.length === 0 ? (
              <FounderEmptyState message="No publishers match this view." />
            ) : null}
            {!loading && !error && visible.length > 0 ? (
              <div className="oac-founder-table-wrap">
                <table className="oac-founder-table">
                  <thead>
                    <tr>
                      <th>Publisher</th>
                      <th>Status</th>
                      <th>Apps</th>
                      <th aria-label="Actions"> </th>
                    </tr>
                  </thead>
                  <tbody>
                    {visible.map((row) => (
                      <tr className={selectedId === row.id ? "oac-founder-table__row--active" : ""} key={row.id}>
                        <td>{row.display_name || row.full_name}</td>
                        <td>
                          <span
                            className={
                              row.account_status === "active"
                                ? "oac-founder-badge oac-founder-badge--healthy"
                                : "oac-founder-badge oac-founder-badge--problem"
                            }
                          >
                            {row.account_status}
                          </span>
                        </td>
                        <td>{row.published_applications}</td>
                        <td>
                          <FounderIconButton
                            kind="view"
                            label={`Inspect ${row.display_name || row.full_name}`}
                            onClick={() => openPublisher(row.id)}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : null}
          </>
        }
        secondary={
          selected ? (
            <div className="oac-founder-ops-detail">
              <p className="oac-founder-ops-detail__meta">{selected.email}</p>
              <p className="oac-founder-ops-detail__meta">
                Agreement: {selected.agreement_version ?? "—"}
              </p>
              <p className="oac-founder-ops-detail__meta">
                Published apps: {selected.published_applications}
              </p>
              {publisherDetail ? (
                <>
                  <p className="oac-founder-ops-list__title">Apps</p>
                  <ul className="oac-founder-ops-list oac-founder-ops-list--compact">
                    {publisherDetail.applications.slice(0, 8).map((app) => (
                      <li className="oac-founder-ops-list__item" key={String(app.id)}>
                        <span className="oac-founder-ops-list__title">{String(app.name)}</span>
                        <span className="oac-founder-ops-list__meta">{String(app.status)}</span>
                      </li>
                    ))}
                  </ul>
                  <p className="oac-founder-ops-list__title">Memberships</p>
                  <ul className="oac-founder-ops-list oac-founder-ops-list--compact">
                    {publisherDetail.subscriptions.slice(0, 6).map((sub) => (
                      <li className="oac-founder-ops-list__item" key={String(sub.id)}>
                        <span className="oac-founder-ops-list__title">{String(sub.application_name)}</span>
                        <span className="oac-founder-ops-list__meta">
                          {String(sub.plan_name)} · {String(sub.status)} · Expires {String(sub.expires_at ?? "—")}
                        </span>
                      </li>
                    ))}
                  </ul>
                  <p className="oac-founder-ops-list__title">Recent payments</p>
                  <ul className="oac-founder-ops-list oac-founder-ops-list--compact">
                    {publisherDetail.payments.slice(0, 6).map((pay) => (
                      <li className="oac-founder-ops-list__item" key={String(pay.id)}>
                        <span className="oac-founder-ops-list__title">{String(pay.amount)} {String(pay.currency)}</span>
                        <span className="oac-founder-ops-list__meta">{String(pay.status)}</span>
                      </li>
                    ))}
                  </ul>
                </>
              ) : (
                <p className="oac-founder-ops-panel__empty">Loading publisher detail...</p>
              )}
              <div className="oac-founder-ops-detail__actions">
                {selected.account_status === "active" ? (
                  <FounderIconButton
                    kind="pause"
                    label={`Suspend ${selected.display_name || selected.full_name}`}
                    onClick={() => setConfirmAction("suspended")}
                  />
                ) : (
                  <FounderIconButton
                    kind="restore"
                    label={`Restore ${selected.display_name || selected.full_name}`}
                    onClick={() => setConfirmAction("active")}
                  />
                )}
              </div>
              {confirmAction ? (
                <FounderConfirmPublish
                  message={`Confirm ${confirmAction === "active" ? "restore" : "suspend"}?`}
                  onCancel={() => setConfirmAction(null)}
                  onConfirm={() => applyStatus(confirmAction)}
                  submitting={submitting}
                />
              ) : null}
              {result ? <p className="oac-founder-ops-detail__result">{result}</p> : null}
            </div>
          ) : undefined
        }
        secondaryTitle={selected?.display_name || selected?.full_name}
      />
    </FounderOperationalShell>
  );
}
